import React, { useEffect, useCallback, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';
import {
  makeCurrentSubmission,
  setCurrentSubmission,
  setNavigateToSubmission,
} from '@actions/submissionActions';
import { MgState, UserEntityInterface } from '@types';
import { Paths } from '@constants/paths';
import {
  currentSubmissionSelector,
  isOriginEditableSelector,
  submissionsIdsSelector,
  submissionUsersDataSelector,
} from '@selectors/submission';
import {
  submissionEnvironmentAttachmentsSelector,
  submissionEnvironmentSelector,
} from '@selectors/submissionEnvironment';
import {
  setRendererWidth,
  setScale,
  setSubmissionAttachments,
  setSubmissionContext,
  setSubmissionEnvironmentLanguage,
} from '@actions/submissionEnvironmentActions';
import { CircularProgress, Typography } from '@material-ui/core';
import { TemplateContainer } from '@components/Pages/LettersPage/Template';
import { ImageLoader } from '@sharedComponents/ImageLoader/ImageLoader';
import {
  createPersonImageAttach,
  convertImageToBase64,
  generateInitialsAvatar,
  translate,
} from '@utils';
import { LangEnum, ImageShape } from '@enums';

import './index.scss';
import { EditorContainer } from '@components/Pages/LettersPage/EditorContainer';

export const SubmissionContent = () => {
  const [isSubmissionNotFound, setIsSubmissionNotFound] = useState<boolean>(false);
  const dispatch = useDispatch();
  const { id } = useParams();
  const navigate = useNavigate();
  const submission = useSelector(currentSubmissionSelector);
  const env = useSelector(submissionEnvironmentSelector);
  const submissionUsers = useSelector(submissionUsersDataSelector);
  const submissionsIds = useSelector(submissionsIdsSelector);
  const submissionEnvironmentAttachments = useSelector(submissionEnvironmentAttachmentsSelector);
  const isOriginUpdatable = useSelector(isOriginEditableSelector);

  const isCurrentSubmissionLoading = useSelector<MgState, boolean>(
    ({ submission }) => submission.isCurrentSubmissionLoading
  );

  const navigateToSubmission = useSelector<MgState, string>(
    ({ submission }) => submission.navigateToSubmission
  );

  const lang = useSelector<MgState, LangEnum>(
    ({ submission }) => submission.currentSubmission?.origin.lang
  );
  const rendererContainer = document.querySelector('#renderer');
  const rendererContainerIsSmall = rendererContainer && rendererContainer.clientWidth < 1270;
  const rendererContainerWidthIsCorrect =
    rendererContainerIsSmall && env.rendererWidth !== rendererContainer.clientWidth;

  const listenToChange = useCallback(
    (data) => {
      dispatch(setSubmissionContext(data));
      dispatch(setCurrentSubmission({ ...submission, data: JSON.stringify(data) }));
    },
    [env, submission]
  );

  useEffect(() => {
    if (id) {
      if (submissionsIds.includes(id)) {
        if (isSubmissionNotFound) {
          setIsSubmissionNotFound(false);
        }
        dispatch(makeCurrentSubmission(id));
      } else {
        setIsSubmissionNotFound(true);
      }
    }
  }, [id]);

  useEffect(() => {
    if (navigateToSubmission) {
      navigate(`${Paths.LETTERS}/${navigateToSubmission}`);
      dispatch(setNavigateToSubmission(null));
    }
  }, [navigateToSubmission]);

  useEffect(() => {
    if (lang && lang !== env.lang) {
      dispatch(setSubmissionEnvironmentLanguage(lang));
    }
  }, [lang]);

  if (rendererContainerWidthIsCorrect) {
    dispatch(setScale((rendererContainer.clientWidth - 10) / 1270));
    dispatch(setRendererWidth(rendererContainer.clientWidth));
  }

  const handleOnImageLoad = (event, id) => {
    if (!submissionEnvironmentAttachments.some((item) => item.name === `${id}.png`)) {
      dispatch(
        setSubmissionAttachments([
          ...submissionEnvironmentAttachments,
          createPersonImageAttach(id, {
            [ImageShape.SQUARE]: convertImageToBase64(event.target, ImageShape.SQUARE),
            [ImageShape.CIRCLE]: convertImageToBase64(event.target, ImageShape.CIRCLE),
          }),
        ])
      );
    }
  };
  const handleOnImageError = (
    event: React.SyntheticEvent<HTMLImageElement, Event>,
    user: UserEntityInterface
  ) => {
    if (!submissionEnvironmentAttachments.some((item) => item.name === `${user.id}.png`)) {
      const initials = lang === LangEnum.RU ? user?.fullNameRu : user?.fullNameEng;
      const squareAvatar = generateInitialsAvatar(initials, ImageShape.SQUARE);
      const circleAvatar = generateInitialsAvatar(initials, ImageShape.CIRCLE);
      dispatch(
        setSubmissionAttachments([
          ...submissionEnvironmentAttachments,
          createPersonImageAttach(user.id, {
            [ImageShape.SQUARE]: squareAvatar,
            [ImageShape.CIRCLE]: circleAvatar,
          }),
        ])
      );
    }
  };

  return (
    <>
      {isCurrentSubmissionLoading ? (
        <CircularProgress />
      ) : (
        <>
          {submissionUsers.map((user) => (
            <ImageLoader
              key={user.id}
              userId={user.id}
              onLoad={(event) => handleOnImageLoad(event, user.id)}
              onError={(event) => handleOnImageError(event, user)}
            />
          ))}
          <div className="main-content">
            <div className="template-container" id="renderer" data-testid="template-renderer-test">
              {isSubmissionNotFound ? (
                <Typography
                  className="main-block__message main-block__message-template"
                  variant="h2"
                >
                  {translate('no_template')}
                </Typography>
              ) : (
                <TemplateContainer env={env} />
              )}
            </div>
            <div className="dividing-line" />
            {isSubmissionNotFound ? (
              <div className="form-wrapper">
                <Typography className=" main-block__message main-block__message-form">
                  {translate('no_form')}
                </Typography>
              </div>
            ) : (
              <EditorContainer
                onFormChange={listenToChange}
                lang={lang}
                isOriginUpdatable={isOriginUpdatable}
              />
            )}
          </div>
        </>
      )}
    </>
  );
};
